import $ from 'zepto';
import { onDocumentReady, expose } from '../main';
import {
  focusableSelector,
  disableTabClass,
  disableTabSelector,
  hideElementEnhanceClass,
  showElementEnhanceClass,
  showPPVXElementEnhanceClass,
  hidePPVXElementEnhanceClass,
} from './constants/classes';
import { DATA_TABINDEX } from './constants/dataKeys';

const $body = $(document.body);

/**
 * creates a curried event handler that will make the toggle slide
 *
 * @param {Zepto} $targetElement
 * @param {Zepto} $triggerElement
 * @param {Number} duration how much time the animation will take
 * @param {Function} callback if you need to do something else with event
 * @returns {Function} the event handler
 */
export const toggleSlideElement = ($targetElement, $triggerElement, duration, callback) => (evt) => {
  const isSlideDown = $targetElement.attr('aria-hidden') === 'true';
  if (isSlideDown) {
    $targetElement
      .attr('aria-hidden', false)
      .slideDown(duration);
    $triggerElement.attr('aria-expanded', true);
  } else {
    $targetElement
      .attr('aria-hidden', true)
      .slideUp(duration);
    $triggerElement.attr('aria-expanded', false);
  }
  if (typeof callback === 'function') {
    callback({
      evt,
      $targetElement,
      $triggerElement,
      isSlideDown,
    });
  }
};

/**
 * initialize the target and the trigger element, and attach the event handler
 *
 * @param {Zepto} config.$targetElement
 * @param {Zepto} config.$triggerElement
 * @param {Number} config.duration how much time the animation will take
 * @param {Function} config.callback if you need to do something else with event
 * @param {Boolen} config.open determines if the default states is open or close
 */
export const initToggleSlideElement = ({
  $targetElement,
  $triggerElement,
  duration,
  callback,
  open = false,
}) => {
  if (open) {
    $targetElement
      .attr('aria-hidden', false)
      .show();
    $triggerElement.attr('aria-expanded', true);
  } else {
    $targetElement
      .attr('aria-hidden', true)
      .hide();
    $triggerElement.attr('aria-expanded', false);
  }
  $triggerElement.click(toggleSlideElement($targetElement, $triggerElement, duration, callback));
};

/**
 * initialize all the toggle slide elements
 *
 * @param {number} duration how much time the animation will take
 */
export const initToggleSlideElements = ({ duration, $context } = {}) => {
  if (!$context) {
    throw new Error('options.$context is required');
  }
  const triggerSelector = '.xvx-toggle-slide-trigger, .xppvx_toggle-slide-trigger';
  function each(index, element) {
    const $trigger = $(element);
    const idTarget = $trigger.data('target');

    if (!idTarget) {
      throw new Error('The trigger element needs its target id');
    }
    $trigger.css('display', 'initial');

    const $target = $(`#${idTarget}`);

    initToggleSlideElement({
      $targetElement: $target,
      $triggerElement: $trigger,
      duration,
    });
  }

  $context.find(triggerSelector).each(each);
};

const hideElementEnhance = ($context) => {
  // Support PPVX enhance utility
  $context.find(`.${hidePPVXElementEnhanceClass}, .${hideElementEnhanceClass}`).css('display', 'none');
};

const showElementEnhance = ($context) => {
  // Support PPVX enhance utility
  $context.find(`.${showPPVXElementEnhanceClass}`).removeClass(showPPVXElementEnhanceClass);
  $context.find(`.${showElementEnhanceClass}`).removeClass(showElementEnhanceClass);
};

export const initElementEnhance = ($context) => {
  hideElementEnhance($context);
  showElementEnhance($context);
};

export const getTarget = ($trigger) => {
  const idTarget = $trigger.data('target');

  if (!idTarget) {
    return undefined;
  }

  const $target = $(`#${idTarget}`);

  if ($target.length === 0) {
    return undefined;
  }

  return $target;
};

/**
 * disable tab action in the focusable elements within the given context
 *
 * @param {Zepto} [$context=$(document.body)]
 * @returns {Zepto} the given context
 */

export const disableTabs = ($context = $body) => $context
  .find(focusableSelector)
  .each((index, element) => {
    const $element = $(element);
    const tabIndex = $element.attr('tabindex');
    if (tabIndex) {
      $element.data(DATA_TABINDEX, tabIndex);
    }

    $element.attr('tabindex', -1);
    $element.addClass(disableTabClass);
  });

/**
 * enables tab action in the focusable elements within the given context
 * only if `xvx-tabindex` data attribute is found
 *
 * @param {Zepto} [$context=$(document.body)]
 * @returns {Zepto} the given context
 */
export const enableTabs = ($context = $body) => $context
  .find(disableTabSelector)
  .each((index, element) => {
    const $element = $(element);
    const oldTabIndex = $element.attr(`data-${DATA_TABINDEX}`);
    if (oldTabIndex) {
      $element.attr('tabindex', oldTabIndex);
    } else {
      $element.removeAttr('tabindex');
    }

    $element.removeClass(disableTabClass);
    $element.data(DATA_TABINDEX, undefined);
  });

export const stopPropagation = (event) => event.stopPropagation();

/**
 *
 * Returns a function which, will not be execute until
 * `waitMilliseconds` have passed
 *
 * @example
 * $(window).on('scroll', debounce(event => {
 *   ...
 * }, 2000));
 * @param {Function} callback
 * @param {Number} waitMilliseconds
 * @returns {Function}
 */
export const debounce = (callback, waitMilliseconds) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      callback(...args);
    }, waitMilliseconds);
  };
};

/**
 * Returns if element is visible using zepto comparison
 * @method isVisible
 * @param {Zepto} element
 * @returns {Boolean}
*/
export function isVisible(element) {
  const $element = $(element);
  return $element.css('display') !== 'none'
    && $element.css('visibility') !== 'hidden'
    && $element.height() > 0;
}

/**
 * like parseInt but with invalid strings return 0
 * instead of NaN
 *
 * @method safeParseInt
 * @param {string} str
 * @param {number} radix An integer between 2 and 36 that represents the radix
*/
export function safeParseInt(str, radix) {
  const number = parseInt(str, radix);
  if (Number.isNaN(number)) {
    return 0;
  }

  return number;
}

/**
 * creates an unique id
 * @memberof! getNewId
 * @returns {string}
 */
export const getNewId = (() => {
  let idCounter = 0;
  return () => {
    const now = Date.now();
    idCounter += 1;
    return `xvx-id-${idCounter}${now}`;
  };
})();

onDocumentReady('Utils', () => {
  initToggleSlideElements({ $context: $body });
  initElementEnhance($body);
  disableTabs($body);
  enableTabs($body);
});

const createLog = (message) => `XVX - ${message}`;

const createLogType = (type) => (message) => {
  /* eslint-disable no-console */
  if (window.console && console[type]) {
    console[type](createLog(message));
  }
};

export const log = {
  error: createLogType('error'),
  warn: createLogType('warn'),
};

export const assert = (value, message) => {
  if (value === undefined) {
    throw new Error(createLog(message));
  }
};

export const parseJSON = (string, defaultValue = {}) => {
  try {
    return JSON.parse(string);
  } catch (error) {
    log.error(error.message);
  }

  return defaultValue;
};

export const isQueryCollection = ($collection) => $collection instanceof $.zepto.Z;

// eslint-disable-next-line no-prototype-builtins
export const objectHasOwnProperty = (object, key) => object.hasOwnProperty(key);

/**
 * Add scroll animation
 * @param {string} targetId
 * @param {string} targetScrollId
 * @return empty
 */
export const scrollAnimation = ((targetId, targetScrollId = '') => {
  const $target = $(targetId);
  if (!$target.length) {
    return;
  }
  const $scrollElement = $(targetScrollId);

  const focusTarget = () => {
    $target.attr('tabindex', '-1'); // Adding tabindex for elements not focusable
    $target.focus();
  };

  if ($scrollElement.length) {
    $scrollElement.get(0).scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  } else {
    $target.get(0).scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }
  let isScrollingIntoView = true;

  $(window).on('scroll', debounce(() => {
    if (isScrollingIntoView) {
      isScrollingIntoView = false;
      focusTarget();
    }
  }, 150));
});

expose({ scrollAnimation });

export default {
  debounce,
  toggleSlideElement,
  initToggleSlideElement,
  initToggleSlideElements,
  initElementEnhance,
  getTarget,
  enableTabs,
  disableTabs,
  stopPropagation,
  isVisible,
  objectHasOwnProperty,
};
